import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Compass, Heart, X, Sparkles, ArrowRight } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { formatApiErrorDetail } from "../lib/api";

export default function Landing() {
  const { login, register } = useAuth();
  const nav = useNavigate();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "login") await login(form.email, form.password);
      else await register(form.name, form.email, form.password);
      nav("/assessment");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-brand-bg">
      <div className="max-w-6xl mx-auto px-4 md:px-6 py-6 md:py-10 grid lg:grid-cols-2 gap-10 items-center min-h-screen">
        {/* Left: hero */}
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="flex items-center gap-2 mb-6">
            <div className="bg-brand-primary border-2 border-black rounded-lg p-1.5 shadow-nb-xs">
              <Compass className="w-6 h-6 text-white" />
            </div>
            <span className="font-heading text-2xl font-bold text-black">TalentSwipe<span className="text-brand-primary">.us</span></span>
          </div>
          <span className="nb-badge bg-brand-yellow mb-5">
            <Sparkles className="w-4 h-4" /> Powered by O*NET
          </span>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-black leading-[1.05] mb-5">
            Swipe the things you <span className="text-brand-primary">love</span>. Discover careers you'll <span className="text-brand-secondary">thrive</span> in.
          </h1>
          <p className="text-base md:text-lg font-semibold text-slate-700 mb-8 max-w-md">
            Rate real work activities from the O*NET database with a fun swipe deck. We map your favourites to matching professions, complete with salary, outlook and people just like you.
          </p>
          <div className="flex flex-wrap gap-3">
            <span className="nb-badge bg-white"><Heart className="w-4 h-4 text-brand-primary" /> Like it</span>
            <span className="nb-badge bg-white"><X className="w-4 h-4" /> Skip it</span>
            <span className="nb-badge bg-brand-purple"><Sparkles className="w-4 h-4" /> Super love</span>
          </div>
        </motion.div>

        {/* Right: auth card */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="nb-card p-6 md:p-8 max-w-md w-full mx-auto"
        >
          <div className="flex gap-2 mb-6 bg-brand-bg border-2 border-black rounded-xl p-1">
            <button
              data-testid="tab-login"
              onClick={() => { setMode("login"); setError(""); }}
              className={`flex-1 py-2 rounded-lg font-bold transition-all ${mode === "login" ? "bg-brand-yellow border-2 border-black shadow-nb-xs" : ""}`}
            >
              Log In
            </button>
            <button
              data-testid="tab-register"
              onClick={() => { setMode("register"); setError(""); }}
              className={`flex-1 py-2 rounded-lg font-bold transition-all ${mode === "register" ? "bg-brand-yellow border-2 border-black shadow-nb-xs" : ""}`}
            >
              Sign Up
            </button>
          </div>

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <div>
                <label className="text-sm font-bold uppercase tracking-wider block mb-1">Name</label>
                <input
                  data-testid="input-name"
                  className="nb-input"
                  placeholder="Alex Rivera"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                />
              </div>
            )}
            <div>
              <label className="text-sm font-bold uppercase tracking-wider block mb-1">Email</label>
              <input
                data-testid="input-email"
                type="email"
                className="nb-input"
                placeholder="you@email.com"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
              />
            </div>
            <div>
              <label className="text-sm font-bold uppercase tracking-wider block mb-1">Password</label>
              <input
                data-testid="input-password"
                type="password"
                className="nb-input"
                placeholder="••••••••"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                required
              />
            </div>

            {error && (
              <div data-testid="auth-error" className="border-2 border-black rounded-xl bg-brand-primary/20 px-4 py-2 text-sm font-bold text-black">
                {error}
              </div>
            )}

            <button
              data-testid="auth-submit"
              type="submit"
              disabled={loading}
              className="nb-btn bg-brand-primary text-white w-full flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {loading ? "Please wait…" : mode === "login" ? "Log In" : "Create Account"}
              <ArrowRight className="w-5 h-5" />
            </button>
          </form>
        </motion.div>
      </div>
    </div>
  );
}
